import { toPng } from 'html-to-image';

const GOOGLE_TILES_SELECTOR = '.atlas-google-tiles';
const PLAIN_BASEMAP_BACKGROUND = '#f2efe9';

export function containsGoogleTiles(mapContainer: HTMLElement): boolean {
  return mapContainer.querySelector(GOOGLE_TILES_SELECTOR) !== null;
}

export function shouldCaptureNode(node: Element, excludeBasemap: boolean): boolean {
  if (!node.classList) return true;
  if (excludeBasemap && node.classList.contains('leaflet-tile-pane')) return false;
  return !node.classList.contains('leaflet-control-container') &&
         !node.classList.contains('leaflet-control') &&
         !node.classList.contains('leaflet-attribution');
}

/**
 * Generate a high-quality thumbnail of the map
 * @param mapContainer - The Leaflet map container element
 * @returns Promise<Blob | null> - PNG thumbnail blob or null if failed
 */
export async function generateMapThumbnail(mapContainer: HTMLElement): Promise<Blob | null> {
  if (!mapContainer) {
    console.error('Map container not provided');
    return null;
  }

  try {
    console.log('Starting thumbnail generation...');
    
    // Wait for tiles to load and settle
    await waitForMapToSettle();
    
    // Temporarily disable animations and transitions
    const originalStyles = disableAnimations();
    
    // Create overlay to hide UI elements during capture
    const overlay = createCaptureOverlay();
    document.body.appendChild(overlay);
    
    // Optimized capture options for smaller file size
    const maxWidth = 400;  // Limit thumbnail width
    const maxHeight = 300; // Limit thumbnail height
    const excludeBasemap = containsGoogleTiles(mapContainer);
    const scale = Math.min(maxWidth / mapContainer.clientWidth, maxHeight / mapContainer.clientHeight, 1);
    
    const options = {
      cacheBust: true,
      useCORS: true,
      allowTaint: false,
      width: Math.floor(mapContainer.clientWidth * scale),
      height: Math.floor(mapContainer.clientHeight * scale),
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: `${mapContainer.clientWidth}px`,
        height: `${mapContainer.clientHeight}px`,
      },
      backgroundColor: excludeBasemap ? PLAIN_BASEMAP_BACKGROUND : undefined,
      filter: (node: Element) => shouldCaptureNode(node, excludeBasemap),
    };

    console.log('Capturing map with options:', options);
    
    // Capture the map
    const dataUrl = await toPng(mapContainer, options);
    
    // Convert to blob
    const response = await fetch(dataUrl);
    const thumbnailBlob = await response.blob();
    
    console.log('Thumbnail generated successfully', {
      size: thumbnailBlob.size,
      type: thumbnailBlob.type
    });
    
    // Cleanup
    restoreAnimations(originalStyles);
    if (overlay.parentNode) {
      document.body.removeChild(overlay);
    }
    
    return thumbnailBlob;
    
  } catch (error) {
    console.error('Error generating thumbnail:', error);
    
    // Cleanup on error
    try {
      const overlay = document.querySelector('.thumbnail-capture-overlay');
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    } catch (cleanupError) {
      console.error('Error during cleanup:', cleanupError);
    }
    
    return null;
  }
}

/**
 * Wait for map tiles to load and animations to settle
 */
async function waitForMapToSettle(): Promise<void> {
  return new Promise((resolve) => {
    // Wait a bit for tiles to load and animations to complete
    setTimeout(resolve, 1000);
  });
}

/**
 * Disable animations and transitions temporarily
 */
function disableAnimations(): { transition: string; animation: string } {
  const originalStyles = {
    transition: document.body.style.transition,
    animation: document.body.style.animation
  };
  
  // Add CSS to disable all animations
  const style = document.createElement('style');
  style.id = 'thumbnail-capture-styles';
  style.textContent = `
    *, *::before, *::after {
      animation-duration: 0s !important;
      animation-delay: 0s !important;
      transition-duration: 0s !important;
      transition-delay: 0s !important;
    }
  `;
  document.head.appendChild(style);
  
  return originalStyles;
}

/**
 * Restore original animation styles
 */
function restoreAnimations(originalStyles: { transition: string; animation: string }): void {
  // Remove the disable styles
  const style = document.getElementById('thumbnail-capture-styles');
  if (style) {
    document.head.removeChild(style);
  }
  
  // Restore original body styles
  document.body.style.transition = originalStyles.transition;
  document.body.style.animation = originalStyles.animation;
}

/**
 * Create an overlay to hide UI elements during capture
 */
function createCaptureOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'thumbnail-capture-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: transparent;
    z-index: 9999;
    pointer-events: none;
  `;
  return overlay;
}

/**
 * Generate thumbnail with retry logic
 * @param mapContainer - The map container element
 * @param maxRetries - Maximum number of retry attempts
 * @returns Promise<Blob | null>
 */
export async function generateMapThumbnailWithRetry(
  mapContainer: HTMLElement, 
  maxRetries: number = 3
): Promise<Blob | null> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`Thumbnail generation attempt ${attempt}/${maxRetries}`);
    
    const result = await generateMapThumbnail(mapContainer);
    
    if (result) {
      return result;
    }
    
    if (attempt < maxRetries) {
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  console.error('Failed to generate thumbnail after', maxRetries, 'attempts');
  return null;
} 